import { Typography, TextStylePreset } from '@vivaldis/antd-ui'
import { useGenderOptions, useTheme } from '@vivaldis/ui'
import { differenceInYears } from 'date-fns'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDateFormat } from '@vivaldis/common'
import { EmployeeProfileQuery_employee } from '../../../../typings/EmployeeProfileQuery'
import { Information } from '../../../../../Information'
import { MissingInformationTag } from '../../../MissingInformationTag'

export interface EmployeeProfileProps {
  employee: EmployeeProfileQuery_employee
}

export const MainInfo: FC<EmployeeProfileProps> = ({ employee }) => {
  const [t] = useTranslation('web_ui')

  const theme = useTheme()

  const { gender, ssn } = employee

  const employeeName = useMemo(
    () => `${employee.firstName} ${employee.lastName}`,
    [employee.firstName, employee.lastName]
  )

  const genderOptions = useGenderOptions()

  const genderText = useMemo(() => {
    return genderOptions.find(genderOption => genderOption.value === gender)
      ?.label
  }, [gender, genderOptions])

  const nationalityName = employee.profile?.nationalityCountry?.nationality
  const birthdate = employee.profile?.birthdate

  const format = useDateFormat('d MMMM yyyy')

  const dateOfBirth = useMemo(() => {
    return birthdate ? format(new Date(birthdate)) : ''
  }, [birthdate, format])

  const age = useMemo(() => {
    return birthdate ? differenceInYears(new Date(), new Date(birthdate)) : ''
  }, [birthdate])

  return (
    <>
      <Information.InformationField label={t('employee_profile.name')}>
        {employeeName}
      </Information.InformationField>
      <Information.InformationField label={t('employee_profile.birthdate')}>
        {birthdate ? (
          <>
            {dateOfBirth}{' '}
            <Typography.Text
              preset={TextStylePreset.Regular14}
              color={theme.gray7}
            >
              ({age})
            </Typography.Text>
          </>
        ) : (
          <MissingInformationTag />
        )}
      </Information.InformationField>
      <Information.InformationField label={t('employee_profile.gender')}>
        {genderText ?? <MissingInformationTag />}
      </Information.InformationField>

      <Information.InformationField label={t('employee_profile.nationality')}>
        {nationalityName ?? <MissingInformationTag />}
      </Information.InformationField>

      <Information.InformationField label={t('employee_profile.ssn')}>
        {ssn ?? <MissingInformationTag />}
      </Information.InformationField>
    </>
  )
}
